<template>
  <div class="files-page">
    <!-- Page Header -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <h3 class="page-title">
            <v-icon small class="mr-1">mdi-file-multiple</v-icon>
            Shared Files
          </h3>
          <div class="file-count" v-if="!loading">
            {{ filesToDisplay.length }}
          </div>
        </div>
        <div class="header-actions">
          <v-btn
            small
            dark
            color="white"
            class="mark-completed-btn green--text"
            @click="openMarkCompletedDialog"
            v-if="filesToDisplay.some((f) => f.selected)"
          >
            <v-icon left small>mdi-check-all</v-icon>
            Mark as Completed ({{
              filesToDisplay.filter((f) => f.selected).length
            }})
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Controls Bar -->
    <div class="controls-bar">
      <div class="controls-left">
        <!-- View Toggle -->
        <div class="view-toggle-wrapper">
          <v-btn
            :class="{ active: viewMode === 'card' }"
            flat
            small
            @click="viewMode = 'card'"
            class="ml-2"
          >
            <v-icon small class="mr-1">mdi-view-grid</v-icon>
            Cards
          </v-btn>
          <v-btn
            :class="{ active: viewMode === 'list' }"
            flat
            small
            @click="viewMode = 'list'"
            class="mr-2"
          >
            <v-icon small class="mr-1">mdi-format-list-bulleted</v-icon>
            List
          </v-btn>
        </div>

        <v-divider vertical class="mx-2" v-if="viewMode === 'card'"></v-divider>

        <!-- Filter Chips (only in card mode) -->
        <div class="filter-chips" v-if="viewMode === 'card'">
          <v-chip
            :color="showCompleted ? 'success' : 'grey lighten-2'"
            :text-color="showCompleted ? 'white' : 'grey darken-1'"
            @click="
              showCompleted = !showCompleted;
              filterChanged();
            "
            small
            class="filter-chip"
          >
            <v-icon left small>{{
              showCompleted
                ? "mdi-checkbox-marked"
                : "mdi-checkbox-blank-outline"
            }}</v-icon>
            Completed
          </v-chip>
          <v-chip
            :color="showExpired ? 'warning' : 'grey lighten-2'"
            :text-color="showExpired ? 'white' : 'grey darken-1'"
            @click="
              showExpired = !showExpired;
              filterChanged();
            "
            small
            class="filter-chip"
          >
            <v-icon left small>{{
              showExpired ? "mdi-checkbox-marked" : "mdi-checkbox-blank-outline"
            }}</v-icon>
            Expired
          </v-chip>
        </div>
      </div>

      <!-- List View Search/Filters -->
      <div class="controls-right" v-if="viewMode === 'list'">
        <v-text-field
          v-model="filters.fileName"
          prepend-inner-icon="mdi-magnify"
          placeholder="Search files..."
          clearable
          hide-details
          dense
          solo
          flat
          background-color="grey lighten-4"
          class="search-field"
          @input="applyFilters"
        ></v-text-field>
      </div>
    </div>

    <!-- List View Additional Filters -->
    <div class="list-filters" v-if="viewMode === 'list'">
      <v-text-field
        v-model="filters.sharedBy"
        label="Shared By"
        prepend-inner-icon="mdi-account"
        clearable
        hide-details
        dense
        outlined
        class="filter-field"
        @input="applyFilters"
      ></v-text-field>
      <v-menu
        v-model="filters.uploadedOnMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        min-width="290px"
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            v-model="filters.uploadedOn"
            label="Uploaded After"
            prepend-inner-icon="mdi-calendar"
            readonly
            clearable
            hide-details
            dense
            outlined
            class="filter-field"
            v-on="on"
            @click:clear="
              filters.uploadedOn = null;
              applyFilters();
            "
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="filters.uploadedOn"
          @input="
            filters.uploadedOnMenu = false;
            applyFilters();
          "
          color="success"
          :max="today"
        ></v-date-picker>
      </v-menu>
      <v-select
        v-model="filters.status"
        :items="statusOptions"
        label="Status"
        prepend-inner-icon="mdi-filter-variant"
        multiple
        small-chips
        deletable-chips
        hide-details
        dense
        outlined
        class="filter-field status-filter"
        @change="applyFilters"
      ></v-select>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <v-progress-circular
        indeterminate
        color="success"
        size="32"
        width="3"
      ></v-progress-circular>
      <div class="loading-text">Loading...</div>
    </div>

    <!-- Empty State -->
    <div v-else-if="!files.length" class="empty-state">
      <v-icon size="48" color="grey lighten-1">mdi-file-hidden</v-icon>
      <h2>No Files Shared With You</h2>
      <p>Files shared with you will appear here</p>
    </div>

    <!-- No Results State (only when filters are active) -->
    <div
      v-else-if="files.length && !filesToDisplay.length && hasActiveFilters"
      class="empty-state"
    >
      <v-icon size="48" color="grey lighten-1">mdi-file-search</v-icon>
      <h2>No Files Match Your Filters</h2>
      <p>Try adjusting your filter settings</p>
      <v-btn color="success" flat @click="clearFilters">
        <v-icon left>mdi-filter-remove</v-icon>
        Clear Filters
      </v-btn>
    </div>

    <!-- No Active Files State (card mode, no filters, but all files are completed/expired) -->
    <div
      v-else-if="
        viewMode === 'card' &&
          files.length &&
          !filesToDisplay.length &&
          !hasActiveFilters
      "
      class="empty-state"
    >
      <v-icon size="48" color="grey lighten-1">mdi-file-check</v-icon>
      <h2>No Active Files</h2>
      <p>All files have been completed or expired</p>
    </div>

    <!-- Card View -->
    <div class="cards-container" v-else-if="viewMode === 'card'">
      <div
        class="file-card"
        :class="{
          'file-card--expired': isExpired(file),
          'file-card--completed': file.isCompleted,
        }"
        v-for="file in filesToDisplay"
        :key="file.id"
      >
        <!-- Status Badge -->
        <div class="file-card__status">
          <v-chip
            v-if="file.isCompleted"
            color="success"
            text-color="white"
            small
            label
          >
            <v-icon left small>mdi-check</v-icon>
            Completed
          </v-chip>
          <v-chip
            v-else-if="isExpired(file)"
            color="warning"
            text-color="white"
            small
            label
          >
            <v-icon left small>mdi-clock-alert-outline</v-icon>
            Expired
          </v-chip>
        </div>

        <!-- Checkbox for selection -->
        <div
          class="file-card__select"
          v-if="!file.isCompleted && !isExpired(file)"
        >
          <v-checkbox
            hide-details
            color="success"
            v-model="file.selected"
            class="ml-1 mt-1"
          ></v-checkbox>
        </div>

        <!-- File Icon -->
        <div class="file-card__icon">
          <div
            class="icon-wrapper"
            :style="{ backgroundColor: getFileIconBg(file) }"
          >
            <v-icon
              :color="isExpired(file) ? '#999' : getFileIconColor(file)"
              size="22"
              >{{ getFileIcon(file) }}</v-icon
            >
          </div>
        </div>

        <!-- File Name -->
        <div class="file-card__name" :title="file.fileName">
          {{ truncateText(file.fileName, 28) }}
        </div>

        <!-- File Details -->
        <div class="file-card__details">
          <div class="detail-row">
            <v-icon small color="grey">mdi-account</v-icon>
            <span>{{ file.uploadedBy }}</span>
          </div>
          <div class="detail-row">
            <v-icon small color="grey">mdi-calendar-upload</v-icon>
            <span>{{ file.uploadedOn | date }}</span>
          </div>
          <div class="detail-row" :class="{ 'expired-text': isExpired(file) }">
            <v-icon small :color="isExpired(file) ? 'warning' : 'grey'"
              >mdi-calendar-clock</v-icon
            >
            <span>Expires {{ file.expirationDate | date }}</span>
          </div>
          <div class="expires-warning-text">
            Files are deleted after the expiration date.
          </div>
        </div>

        <!-- Instruction (expandable) -->
        <div class="file-card__instruction" v-if="file.instruction">
          <div class="instruction-header">
            <v-icon small color="grey" class="mr-1">mdi-information</v-icon>
            <span class="instruction-label">Instruction</span>
            <v-btn
              v-if="file.instruction.length > 40"
              icon
              x-small
              @click="toggleExpand(file.id, 'instruction')"
              class="expand-btn"
            >
              <v-icon small>{{
                isFieldExpanded(file.id, "instruction")
                  ? "mdi-chevron-up"
                  : "mdi-chevron-down"
              }}</v-icon>
            </v-btn>
          </div>
          <div
            class="instruction-text"
            :class="{ expanded: isFieldExpanded(file.id, 'instruction') }"
          >
            {{
              isFieldExpanded(file.id, "instruction")
                ? file.instruction
                : truncateText(file.instruction, 40)
            }}
          </div>
        </div>

        <!-- Note Section -->
        <div class="file-card__note" v-if="!file.editing">
          <div class="note-header">
            <v-icon small color="grey" class="mr-1">mdi-note-text</v-icon>
            <span class="note-label">Note</span>
            <v-btn
              v-if="!isExpired(file)"
              icon
              x-small
              color="success"
              @click="edit(file)"
              class="edit-btn"
            >
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
          </div>
          <div class="note-text" :title="file.note">
            {{ file.note || "—" }}
          </div>
        </div>

        <!-- Edit Mode -->
        <div class="file-card__edit" v-if="file.editing">
          <v-text-field
            v-model="file.copy.note"
            hide-details
            label="Note"
            outlined
            dense
          ></v-text-field>
          <v-checkbox
            v-model="file.copy.isCompleted"
            label="Mark as Completed"
            color="success"
            hide-details
            class="mt-2"
          ></v-checkbox>
          <div class="edit-actions">
            <v-btn small flat @click="rollback(file)" :disabled="processing"
              >Cancel</v-btn
            >
            <v-btn
              small
              color="success"
              @click="save(file)"
              :loading="processing"
              >Save</v-btn
            >
          </div>
        </div>

        <!-- Actions -->
        <div
          class="file-card__actions"
          v-if="!isExpired(file) && !file.editing"
        >
          <v-btn
            color="success"
            small
            block
            @click="downloadFile(file)"
            :disabled="isMessage(file) || isDeleted(file)"
          >
            <v-icon left small>mdi-download</v-icon>
            Download
          </v-btn>
          <v-btn
            color="success"
            small
            outlined
            block
            class="mt-1"
            @click="viewFile(file)"
            :disabled="isMessage(file) || isDeleted(file)"
          >
            <v-icon left small>mdi-eye</v-icon>
            View
          </v-btn>
        </div>
      </div>
    </div>

    <!-- List View -->
    <div class="list-container" v-else-if="viewMode === 'list'">
      <v-data-table
        :headers="tableHeaders"
        :items="filesToDisplay"
        :rows-per-page-items="[10, 25, 50, 100]"
        :pagination.sync="pagination"
        class="files-table elevation-1"
      >
        <template v-slot:items="props">
          <tr
            :class="{
              'row-expired': isExpired(props.item),
              'row-completed': props.item.isCompleted,
            }"
          >
            <td class="select-cell">
              <v-checkbox
                v-if="!props.item.isCompleted && !isExpired(props.item)"
                v-model="props.item.selected"
                hide-details
                color="success"
                class="mt-0 pt-0"
              ></v-checkbox>
            </td>
            <td class="icon-cell">
              <div
                class="table-icon"
                :style="{ backgroundColor: getFileIconBg(props.item) }"
              >
                <v-icon
                  small
                  :color="
                    isExpired(props.item)
                      ? '#999'
                      : getFileIconColor(props.item)
                  "
                  >{{ getFileIcon(props.item) }}</v-icon
                >
              </div>
            </td>
            <td class="name-cell" :title="props.item.fileName">
              {{ truncateText(props.item.fileName, 30) }}
            </td>
            <td class="email-cell">{{ props.item.uploadedBy }}</td>
            <td class="date-cell">{{ props.item.uploadedOn | date }}</td>
            <td
              class="date-cell"
              :class="{ 'expired-text': isExpired(props.item) }"
            >
              <div>{{ props.item.expirationDate | date }}</div>
              <div class="expires-warning-text">
                Files are deleted after the expiration date.
              </div>
            </td>
            <td class="instruction-cell">
              <span v-if="!isFieldExpanded(props.item.id, 'instruction')">
                <span :title="props.item.instruction">{{
                  truncateText(props.item.instruction, 20)
                }}</span>
                <v-icon
                  v-if="
                    props.item.instruction && props.item.instruction.length > 20
                  "
                  small
                  class="expand-icon"
                  @click="toggleExpand(props.item.id, 'instruction')"
                  >mdi-chevron-down</v-icon
                >
              </span>
              <span v-else class="expanded-text">
                {{ props.item.instruction }}
                <v-icon
                  small
                  class="expand-icon"
                  @click="toggleExpand(props.item.id, 'instruction')"
                  >mdi-chevron-up</v-icon
                >
              </span>
            </td>
            <td class="note-cell" :title="props.item.note">
              {{ truncateText(props.item.note, 20) }}
            </td>
            <td class="status-cell">
              <v-chip
                v-if="props.item.isCompleted"
                color="success"
                text-color="white"
                small
                label
                >Completed</v-chip
              >
              <v-chip
                v-else-if="isExpired(props.item)"
                color="warning"
                text-color="white"
                small
                label
                >Expired</v-chip
              >
              <v-chip v-else color="info" text-color="white" small label
                >Active</v-chip
              >
            </td>
            <td class="actions-cell">
              <template v-if="!isExpired(props.item)">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      v-on="on"
                      @click="downloadFile(props.item)"
                      :disabled="isMessage(props.item) || isDeleted(props.item)"
                    >
                      <v-icon small color="success">mdi-download</v-icon>
                    </v-btn>
                  </template>
                  <span>Download</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      v-on="on"
                      @click="viewFile(props.item)"
                      :disabled="isMessage(props.item) || isDeleted(props.item)"
                    >
                      <v-icon small color="success">mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>View</span>
                </v-tooltip>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      small
                      v-on="on"
                      @click="openEditDialog(props.item)"
                    >
                      <v-icon small color="grey">mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Edit</span>
                </v-tooltip>
              </template>
              <span v-else class="no-actions">—</span>
            </td>
          </tr>
        </template>
        <template v-slot:no-data>
          <div class="no-data-message">
            <v-icon color="grey">mdi-file-search</v-icon>
            <span>No files found</span>
          </div>
        </template>
      </v-data-table>
    </div>

    <!-- Mark Completed Dialog -->
    <v-dialog v-model="markCompletedDialog" max-width="420" persistent>
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          <v-icon color="success" class="mr-2">mdi-check-circle</v-icon>
          Mark as Completed
        </v-card-title>
        <v-card-text class="dialog-content">
          <p>
            You are about to mark
            <strong>{{
              filesToDisplay.filter((f) => f.selected).length
            }}</strong>
            file(s) as completed.
          </p>
          <p class="dialog-note">
            Completed files will be hidden from the default view.
          </p>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn flat @click="markCompletedDialog = false">Cancel</v-btn>
          <v-btn
            color="success"
            @click="markAsCompleted()"
            :loading="processing"
          >
            <v-icon left>mdi-check</v-icon>
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="480" persistent>
      <v-card class="dialog-card" v-if="editingFile">
        <v-card-title class="dialog-title">
          Edit File
        </v-card-title>
        <v-card-text class="dialog-content">
          <div class="edit-file-name">
            <v-icon color="grey" class="mr-2">{{
              getFileIcon(editingFile)
            }}</v-icon>
            {{ editingFile.fileName }}
          </div>
          <v-text-field
            v-model="editingFile.copy.note"
            label="Note"
            outlined
            class="mt-2 mx-1"
          ></v-text-field>
          <v-checkbox
            v-model="editingFile.copy.isCompleted"
            label="Mark as Completed"
            color="success"
            hide-details
            class="my-0"
          ></v-checkbox>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer></v-spacer>
          <v-btn
            flat
            @click="
              editDialog = false;
              editingFile = null;
            "
            :disabled="processing"
            >Cancel</v-btn
          >
          <v-btn
            color="success"
            @click="saveFromDialog()"
            :loading="processing"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";
import { truncate, pluralize, onlyUnique } from "../../utils/helpers.js";

export default {
  data: () => ({
    files: [],
    filesToDisplay: [],
    sharedByList: [],
    sharedBy: "",
    loading: true,
    processing: false,
    showCompleted: false,
    showExpired: false,
    markCompletedDialog: false,
    viewMode: "card",
    expandedFields: {},
    editDialog: false,
    editingFile: null,
    filters: {
      fileName: "",
      sharedBy: "",
      uploadedOn: null,
      uploadedOnMenu: false,
      status: ["active"],
    },
    statusOptions: [
      { text: "Active", value: "active" },
      { text: "Completed", value: "completed" },
      { text: "Expired", value: "expired" },
    ],
    pagination: {
      sortBy: "uploadedOn",
      descending: true,
      rowsPerPage: 10,
    },
    tableHeaders: [
      { text: "", value: "selected", sortable: false, width: "50px" },
      { text: "", value: "icon", sortable: false, width: "50px" },
      { text: "File Name", value: "fileName" },
      { text: "Shared By", value: "uploadedBy" },
      { text: "Uploaded", value: "uploadedOn", width: "110px" },
      { text: "Expires", value: "expirationDate", width: "110px" },
      { text: "Instruction", value: "instruction", sortable: false },
      { text: "Note", value: "note", sortable: false },
      { text: "Status", value: "status", sortable: false, width: "100px" },
      { text: "Actions", value: "actions", sortable: false, width: "130px" },
    ],
    rules: {
      required: (value) => !!value || "Required.",
    },
  }),
  async created() {
    this.loadFiles();
  },
  computed: {
    today() {
      return moment().format("YYYY-MM-DD");
    },
    hasActiveFilters() {
      if (this.viewMode === "card") {
        // In card mode, check if any toggle is ON (showing extra items)
        return this.showCompleted || this.showExpired;
      } else {
        // In list mode, check if any filter is different from default
        const hasFileNameFilter = !!this.filters.fileName;
        const hasSharedByFilter = !!this.filters.sharedBy;
        const hasUploadDateFilter = !!this.filters.uploadedOn;
        const hasNonDefaultStatus =
          !this.filters.status ||
          this.filters.status.length !== 1 ||
          !this.filters.status.includes("active");

        return (
          hasFileNameFilter ||
          hasSharedByFilter ||
          hasUploadDateFilter ||
          hasNonDefaultStatus
        );
      }
    },
  },
  watch: {
    viewMode() {
      this.applyFilters();
    },
  },
  methods: {
    async loadFiles() {
      try {
        const files = await this.$http.get(
          "api/sharedFiles/getFilesSharedWithUser?user=" +
            this.$store.state.authentication.user
        );

        this.files = files.map((file) => {
          file.editing = false;
          file.selected = false;
          return file;
        });

        this.applyFilters();
        this.loading = false;
      } catch (e) {
        this.loading = false;
      }
    },
    isExpired(file) {
      if (!file.expirationDate) return false;
      return moment(file.expirationDate).isBefore(moment());
    },
    isDeleted(file) {
      return file.url === "DELETED";
    },
    filterChanged() {
      this.pagination.sortBy = "uploadedOn";
      this.pagination.descending = true;
      this.applyFilters();
    },
    clearFilters() {
      this.showCompleted = false;
      this.showExpired = false;
      this.filters.fileName = "";
      this.filters.sharedBy = "";
      this.filters.uploadedOn = null;
      this.filters.status = ["active"];
      this.applyFilters();
    },
    applyFilters() {
      let result = this.files;

      if (this.viewMode === "card") {
        // Card mode: use toggle switches
        if (!this.showCompleted) {
          result = result.filter((f) => !f.isCompleted);
        }

        if (!this.showExpired) {
          result = result.filter((f) => !this.isExpired(f));
        }
      } else {
        // List mode: use multi-select status filter
        if (this.filters.fileName) {
          const search = this.filters.fileName.toLowerCase();
          result = result.filter(
            (f) => f.fileName && f.fileName.toLowerCase().includes(search)
          );
        }

        if (this.filters.sharedBy) {
          const search = this.filters.sharedBy.toLowerCase();
          result = result.filter(
            (f) => f.uploadedBy && f.uploadedBy.toLowerCase().includes(search)
          );
        }

        if (this.filters.uploadedOn) {
          const filterDate = moment(this.filters.uploadedOn).startOf("day");
          result = result.filter((f) => {
            const fileDate = moment(f.uploadedOn);
            return fileDate.isSameOrAfter(filterDate);
          });
        }

        if (this.filters.status && this.filters.status.length > 0) {
          result = result.filter((f) => {
            const isActive = !f.isCompleted && !this.isExpired(f);
            const isCompleted = f.isCompleted;
            const isExpired = this.isExpired(f);

            return (
              (this.filters.status.includes("active") && isActive) ||
              (this.filters.status.includes("completed") && isCompleted) ||
              (this.filters.status.includes("expired") && isExpired)
            );
          });
        }
      }

      // Sort by uploadedOn descending
      result.sort((a, b) => {
        const dateA = new Date(a.uploadedOn);
        const dateB = new Date(b.uploadedOn);
        return dateB - dateA;
      });

      this.filesToDisplay = result;
    },
    openMarkCompletedDialog() {
      this.markCompletedDialog = true;
    },
    toggleExpand(fileId, fieldName) {
      if (!this.expandedFields[fileId]) {
        this.$set(this.expandedFields, fileId, {});
      }
      this.$set(
        this.expandedFields[fileId],
        fieldName,
        !this.expandedFields[fileId][fieldName]
      );
    },
    isFieldExpanded(fileId, fieldName) {
      return (
        this.expandedFields[fileId] && this.expandedFields[fileId][fieldName]
      );
    },
    getFileIcon(file) {
      if (!file.url && !file.fileName) return "mdi-message-text";
      if (this.isDeleted(file)) {
        if (file.fileName) {
          return this.getFileIconFromName(file.fileName);
        }
        return "mdi-file-remove";
      }

      switch (this.getFileType(file.url)) {
        case "pdf":
          return "mdi-file-pdf-box";
        case "docx":
        case "doc":
          return "mdi-file-word-box";
        case "xls":
        case "xlsx":
          return "mdi-file-excel-box";
        case "mp3":
        case "wav":
        case "wma":
          return "mdi-file-music";
        case "jpg":
        case "jpeg":
        case "png":
        case "gif":
          return "mdi-file-image";
        case "zip":
        case "rar":
          return "mdi-zip-box";
        default:
          return "mdi-file-document";
      }
    },
    getFileIconFromName(fileName) {
      if (!fileName) return "mdi-file-document";
      const ext = fileName
        .split(".")
        .pop()
        .toLowerCase();
      switch (ext) {
        case "pdf":
          return "mdi-file-pdf-box";
        case "doc":
        case "docx":
          return "mdi-file-word-box";
        case "xls":
        case "xlsx":
          return "mdi-file-excel-box";
        case "mp3":
        case "wav":
        case "wma":
          return "mdi-file-music";
        case "jpg":
        case "jpeg":
        case "png":
        case "gif":
          return "mdi-file-image";
        case "zip":
        case "rar":
          return "mdi-zip-box";
        default:
          return "mdi-file-document";
      }
    },
    getFileIconColor(file) {
      if (!file.url && !file.fileName) return "#5c6bc0";
      if (this.isDeleted(file)) return "#999";

      switch (this.getFileType(file.url)) {
        case "pdf":
          return "#e53935";
        case "doc":
        case "docx":
          return "#1976d2";
        case "xls":
        case "xlsx":
          return "#43a047";
        case "mp3":
        case "wav":
        case "wma":
          return "#8e24aa";
        case "jpg":
        case "jpeg":
        case "png":
        case "gif":
          return "#fb8c00";
        case "zip":
        case "rar":
          return "#fdd835";
        default:
          return "#78909c";
      }
    },
    getFileIconBg(file) {
      if (this.isExpired(file)) return "#f5f5f5";
      const color = this.getFileIconColor(file);
      return color + "15";
    },
    getFileType(url) {
      if (!url || url === "DELETED") return null;
      try {
        const parsedUrl = new URL(url);
        const path = parsedUrl.pathname;
        const match = path.match(/\.([^.?#]+)(?:[?#]|$)/);
        if (!match) return null;
        const extension = match[1].toLowerCase();
        return extension.length > 5 ? null : extension.toLowerCase();
      } catch (e) {
        return null;
      }
    },
    truncateText(fileName, count = 30) {
      return truncate(fileName, count);
    },
    isMessage(file) {
      return !file.url && !file.fileName;
    },
    viewFile(file) {
      if (this.isDeleted(file) || this.isExpired(file)) return;
      window.open(file.url);
    },
    async downloadFile(file) {
      if (this.isDeleted(file) || this.isExpired(file)) return;

      try {
        const response = await fetch(file.url);
        if (!response.ok) throw new Error("Network response was not ok");

        const blob = await response.blob();
        const link = document.createElement("a");
        const blobUrl = window.URL.createObjectURL(blob);

        link.href = blobUrl;
        link.download = file.fileName;
        document.body.appendChild(link);
        link.click();

        document.body.removeChild(link);
        window.URL.revokeObjectURL(blobUrl);
      } catch (error) {
        console.error("Download failed:", error);
        this.$notify({
          text: "Error downloading: " + file.fileName,
          group: "notification",
          type: "error",
        });
      }
    },
    edit(file) {
      file.copy = JSON.parse(JSON.stringify(file));
      file.editing = true;
    },
    openEditDialog(file) {
      file.copy = JSON.parse(JSON.stringify(file));
      this.editingFile = file;
      this.editDialog = true;
    },
    rollback(file) {
      file.editing = false;
    },
    async save(file) {
      try {
        this.processing = true;

        await this.$http.post(
          "api/sharedFiles/update?id=" +
            file.id +
            "&note=" +
            (file.copy.note ? file.copy.note : "") +
            "&isCompleted=" +
            file.copy.isCompleted
        );

        file.editing = false;
        file.note = file.copy.note;
        file.isCompleted = file.copy.isCompleted;

        this.processing = false;
        this.loadFiles();
      } catch (e) {
        this.processing = false;
      }
    },
    async saveFromDialog() {
      if (!this.editingFile) return;

      try {
        this.processing = true;

        await this.$http.post(
          "api/sharedFiles/update?id=" +
            this.editingFile.id +
            "&note=" +
            (this.editingFile.copy.note ? this.editingFile.copy.note : "") +
            "&isCompleted=" +
            this.editingFile.copy.isCompleted
        );

        this.editingFile.note = this.editingFile.copy.note;
        this.editingFile.isCompleted = this.editingFile.copy.isCompleted;

        this.processing = false;
        this.editDialog = false;
        this.editingFile = null;

        this.loadFiles();
      } catch (e) {
        this.processing = false;
      }
    },
    async markAsCompleted() {
      try {
        this.processing = true;

        const ids = this.filesToDisplay
          .filter((f) => f.selected)
          .map((m) => m.id);

        await this.$http.post("api/sharedFiles/markAsCompleted", ids);

        this.processing = false;
        this.markCompletedDialog = false;

        this.loadFiles();

        this.$notify({
          group: "notification",
          text: ids.length + " Files Updated",
          type: "success",
        });
      } catch (e) {
        this.processing = false;
      }
    },
  },
};
</script>

<style scoped>
.files-page {
  padding: 0;
  background-color: #f5f5f5;
  min-height: 100%;
}

/* Page Header */
.page-header {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  padding: 10px 16px;
  margin: 10px;
  border-radius: 5px;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-title {
  color: white;
  font-size: 16px;
  font-weight: 500;
  margin: 0;
  display: flex;
  align-items: center;
}

.page-title .v-icon {
  color: white !important;
}

.file-count {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
}

.mark-completed-btn {
  box-shadow: none;
}

/* Controls Bar */
.controls-bar {
  background: white;
  padding: 6px 12px;
  margin: 0px 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #e0e0e0;
  flex-wrap: wrap;
  gap: 8px;
}

.controls-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.view-toggle-wrapper {
  display: flex;
  background: #f0f0f0;
  border-radius: 4px;
  padding: 1px;
}

.view-toggle-wrapper .v-btn {
  border-radius: 3px !important;
  text-transform: none;
  font-weight: 400;
  min-width: 60px;
  height: 26px !important;
  font-size: 12px;
}

.view-toggle-wrapper .v-btn.active {
  background: white !important;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.filter-chips {
  display: flex;
  gap: 4px;
}

.filter-chip {
  cursor: pointer;
  height: 24px !important;
  font-size: 11px !important;
  padding: 0 12px !important;
}

.filter-chip:hover {
  transform: none;
}

.controls-right {
  flex: 0 0 200px;
}

.search-field {
  border-radius: 4px;
  font-size: 12px;
}

/* List Filters */
.list-filters {
  background: white;
  padding-left: 12px;
  padding-right: 12px;
  padding-top: 0px;
  padding-bottom: 12px;
  display: flex;
  gap: 8px;
  border-bottom: 1px solid #e0e0e0;
  margin-top: 8px;
  margin-bottom: 4px;
  margin-right: 10px;
  margin-left: 10px;
}

.filter-field {
  flex: 1;
  max-width: 160px;
}

.status-filter {
  max-width: 220px;
}

.status-filter >>> .v-chip {
  height: 18px !important;
  font-size: 10px !important;
  margin: 2px !important;
}

.status-filter >>> .v-chip .v-chip__close {
  font-size: 14px !important;
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
}

.loading-text {
  margin-top: 10px;
  color: #666;
  font-size: 12px;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.empty-state h2 {
  margin: 10px 0 4px;
  color: #424242;
  font-weight: 500;
  font-size: 16px;
}

.empty-state p {
  color: #757575;
  margin-bottom: 10px;
  font-size: 13px;
}

/* Cards Container */
.cards-container {
  display: flex;
  flex-wrap: wrap;
  padding: 10px;
  gap: 10px;
}

/* File Card */
.file-card {
  width: 200px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 12px;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: all 0.15s ease;
}

.file-card:hover {
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
}

.file-card--expired {
  opacity: 0.7;
  background: #fafafa;
}

.file-card--completed {
  background: #f9fdf9;
}

.file-card__status {
  position: absolute;
  top: 6px;
  right: 6px;
  margin-bottom: 6px;
}

.file-card__status .v-chip {
  height: 18px !important;
  font-size: 10px !important;
}

.file-card__select {
  position: absolute;
  top: 4px;
  left: 4px;
}

.file-card__select >>> .v-input--selection-controls__input {
  margin-right: 0;
}

.file-card__icon {
  display: flex;
  justify-content: center;
  margin: 24px 0 6px;
}

.icon-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-card__name {
  font-size: 12px;
  font-weight: 600;
  color: #333;
  text-align: center;
  margin-bottom: 8px;
  line-height: 1.2;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-card__details {
  margin-bottom: 6px;
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #666;
  padding: 1px 0;
}

.detail-row .v-icon {
  font-size: 12px !important;
}

.detail-row.expired-text {
  color: #f57c00;
}

.file-card__instruction,
.file-card__note {
  background: #f8f9fa;
  border-radius: 4px;
  padding: 6px 8px;
  margin-bottom: 6px;
}

.instruction-header,
.note-header {
  display: flex;
  align-items: center;
  margin-bottom: 2px;
}

.instruction-header .v-icon,
.note-header .v-icon {
  font-size: 11px !important;
}

.instruction-label,
.note-label {
  font-size: 9px;
  text-transform: uppercase;
  color: #888;
  font-weight: 600;
  flex: 1;
}

.instruction-text,
.note-text {
  font-size: 11px;
  color: #555;
  line-height: 1.3;
}

.instruction-text.expanded {
  white-space: normal;
  word-break: break-word;
}

.expand-btn,
.edit-btn {
  margin-left: auto;
  width: 20px !important;
  height: 20px !important;
}

.file-card__edit {
  background: #f0f7f0;
  border-radius: 4px;
  padding: 8px;
  margin-bottom: 6px;
}

.edit-actions {
  display: flex;
  justify-content: flex-end;
  gap: 4px;
  margin-top: 6px;
}

.file-card__actions {
  margin-top: auto;
  padding-top: 0px;
}

.file-card__actions .v-btn {
  height: 28px !important;
  font-size: 11px;
}

/* List Container */
.list-container {
  padding: 10px;
}

.files-table {
  border-radius: 4px;
  overflow: hidden;
}

.files-table >>> thead th {
  background: #fafafa;
  font-weight: 600;
  color: #555;
  font-size: 11px;
  text-transform: uppercase;
  padding: 6px 8px !important;
  height: 32px !important;
}

.files-table >>> tbody tr {
  transition: background 0.1s;
}

.files-table >>> tbody tr:hover {
  background: #f8f9fa;
}

.files-table >>> tbody td {
  padding: 4px 8px !important;
  font-size: 12px;
  vertical-align: middle;
  height: 36px !important;
}

.row-expired {
  background: #fafafa !important;
  opacity: 0.8;
}

.row-completed {
  background: #f9fdf9 !important;
}

.select-cell {
  width: 36px;
}

.icon-cell {
  width: 40px;
}

.table-icon {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.name-cell {
  font-weight: 500;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.email-cell {
  color: #666;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.date-cell {
  color: #666;
  font-size: 11px;
}

.expires-warning-text {
  color: #f57c00;
  font-size: 10px;
  line-height: 1.2;
  font-weight: 500;
}

.expired-text {
  color: #f57c00 !important;
  font-weight: 500;
}

.instruction-cell,
.note-cell {
  max-width: 120px;
  color: #666;
  font-size: 11px;
}

.expand-icon {
  cursor: pointer;
  margin-left: 2px;
  opacity: 0.6;
  font-size: 14px !important;
}

.expand-icon:hover {
  opacity: 1;
}

.expanded-text {
  white-space: normal;
  word-break: break-word;
}

.status-cell {
  white-space: nowrap;
}

.status-cell .v-chip {
  height: 20px !important;
  font-size: 10px !important;
}

.actions-cell {
  white-space: nowrap;
}

.actions-cell .v-btn {
  width: 26px !important;
  height: 26px !important;
}

.no-actions {
  color: #ccc;
  padding-left: 8px;
  font-size: 11px;
}

.no-data-message {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 16px;
  color: #888;
  font-size: 12px;
}

/* Dialogs */
.dialog-card {
  border-radius: 8px;
}

.dialog-title {
  font-size: 15px;
  font-weight: 500;
  padding: 12px 16px 0px;
}

.dialog-content {
  padding: 8px 16px 12px;
}

.dialog-content p {
  margin: 0 0 6px;
  color: #555;
  font-size: 13px;
}

.dialog-note {
  font-size: 11px;
  color: #888;
}

.edit-file-name {
  display: flex;
  align-items: center;
  background: #f5f5f5;
  padding: 8px;
  border-radius: 4px;
  font-weight: 500;
  font-size: 12px;
}

.dialog-actions {
  padding: 8px 0px 16px;
}
</style>
